// Lire un tableau (D-079) : CSV (séparateur deviné, accents Windows réparés), .xlsx (zip + XML,
// sans bibliothèque : DecompressionStream du navigateur), page web déguisée en .xls (exports
// courants), copier-coller depuis un tableur. Un vrai .xls binaire ancien est refusé avec un
// message clair : l'enregistrer en .xlsx ou en .csv.

export interface Sheet { name: string; rows: string[][] }
export interface ReadResult { sheets: Sheet[]; encoding?: 'utf-8' | 'windows-1252'; kind: 'csv' | 'xlsx' | 'html' | 'paste' }

/** Texte d'un fichier : UTF-8, ou Windows-1252 si l'UTF-8 donne des caractères cassés. */
export function decodeText(bytes: Uint8Array): { text: string; encoding: 'utf-8' | 'windows-1252' } {
  const utf8 = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  if (!utf8.includes('�')) return { text: utf8.replace(/^﻿/, ''), encoding: 'utf-8' };
  return { text: new TextDecoder('windows-1252').decode(bytes), encoding: 'windows-1252' };
}

/** CSV : séparateur deviné sur les premières lignes (; , tabulation), guillemets gérés. */
export function parseCsv(text: string): string[][] {
  const sample = text.split(/\r?\n/).slice(0, 10).join('\n');
  const counts = [';', ',', '\t'].map(sep => [sep, (sample.match(new RegExp(sep === '\t' ? '\t' : `\\${sep}`, 'g')) ?? []).length] as const);
  const sep = counts.sort((a, b) => b[1] - a[1])[0]![1] > 0 ? counts[0]![0] : ';';
  const rows: string[][] = [];
  let row: string[] = [], cell = '', quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else quoted = false; }
      else cell += ch;
    } else if (ch === '"' && cell === '') quoted = true;
    else if (ch === sep) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return trimRows(rows);
}

/** Cellules nettoyées, colonnes vides et lignes vides de la fin retirées. Les lignes vides du
 *  milieu restent à leur place : le numéro d'une ligne est toujours celui du fichier. */
export function trimRows(rows: string[][]): string[][] {
  const clean = rows.map(r => (r ?? []).map(c => String(c ?? '').replace(/ /g, ' ').trim()));
  while (clean.length && !clean[clean.length - 1]!.some(c => c !== '')) clean.pop();
  const width = Math.max(0, ...clean.map(r => r.length));
  const used = Array.from({ length: width }, (_, j) => clean.some(r => (r[j] ?? '') !== ''));
  return clean.map(r => Array.from({ length: width }, (_, j) => r[j] ?? '').filter((_, j) => used[j]));
}

// --- .xlsx : un zip de fichiers XML -----------------------------------------------------------
interface ZipEntry { name: string; method: number; size: number; offset: number }
function zipEntries(buf: Uint8Array): ZipEntry[] {
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 66000); i--) if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error('fichier zip illisible');
  const count = dv.getUint16(eocd + 10, true);
  let p = dv.getUint32(eocd + 16, true);
  const out: ZipEntry[] = [];
  for (let k = 0; k < count; k++) {
    if (dv.getUint32(p, true) !== 0x02014b50) break;
    const method = dv.getUint16(p + 10, true), size = dv.getUint32(p + 20, true);
    const nlen = dv.getUint16(p + 28, true), xlen = dv.getUint16(p + 30, true), clen = dv.getUint16(p + 32, true);
    const offset = dv.getUint32(p + 42, true);
    const name = new TextDecoder().decode(buf.subarray(p + 46, p + 46 + nlen));
    out.push({ name, method, size, offset });
    p += 46 + nlen + xlen + clen;
  }
  return out;
}
async function zipRead(buf: Uint8Array, e: ZipEntry): Promise<string> {
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const nlen = dv.getUint16(e.offset + 26, true), xlen = dv.getUint16(e.offset + 28, true);
  const start = e.offset + 30 + nlen + xlen;
  const data = buf.subarray(start, start + e.size);
  if (e.method === 0) return new TextDecoder().decode(data);
  if (e.method !== 8) throw new Error('compression inconnue');
  const stream = new Blob([new Uint8Array(data)]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return await new Response(stream).text();
}
const unxml = (s: string) => s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'")
  .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n))).replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCharCode(parseInt(n, 16))).replace(/&amp;/g, '&');
const texts = (xml: string) => [...xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map(m => unxml(m[1]!)).join('');
const colIndex = (ref: string) => { let n = 0; for (const ch of ref.replace(/\d+/g, '')) n = n * 26 + (ch.charCodeAt(0) - 64); return n - 1; };

export async function parseXlsx(buf: Uint8Array): Promise<Sheet[]> {
  const entries = zipEntries(buf);
  const get = async (name: string) => { const e = entries.find(x => x.name === name); return e ? zipRead(buf, e) : null; };
  const shared = [...((await get('xl/sharedStrings.xml')) ?? '').matchAll(/<si>([\s\S]*?)<\/si>/g)].map(m => texts(m[1]!));
  const wb = (await get('xl/workbook.xml')) ?? '';
  const rels = (await get('xl/_rels/workbook.xml.rels')) ?? '';
  const sheetsMeta = [...wb.matchAll(/<sheet\b[^>]*\bname="([^"]*)"[^>]*\br:id="([^"]*)"/g)].map(m => ({ name: unxml(m[1]!), rid: m[2]! }));
  const target = (rid: string) => {
    const m = new RegExp(`<Relationship\\b[^>]*\\bId="${rid}"[^>]*\\bTarget="([^"]*)"`).exec(rels) ?? new RegExp(`<Relationship\\b[^>]*\\bTarget="([^"]*)"[^>]*\\bId="${rid}"`).exec(rels);
    const t = m?.[1] ?? '';
    return t.startsWith('/') ? t.slice(1) : `xl/${t}`;
  };
  const list = sheetsMeta.length ? sheetsMeta : [{ name: 'Feuille 1', rid: '' }];
  const out: Sheet[] = [];
  for (const s of list) {
    const xml = await get(s.rid ? target(s.rid) : 'xl/worksheets/sheet1.xml');
    if (!xml) continue;
    const rows: string[][] = [];
    for (const rm of xml.matchAll(/<row\b([^>]*)>([\s\S]*?)<\/row>/g)) {
      const row: string[] = [];
      // La ligne à sa place (les lignes vides sont absentes du XML) : attribut r="numéro".
      const rn = Number(/\br="(\d+)"/.exec(rm[1]!)?.[1] ?? rows.length + 1);
      while (rows.length < rn - 1) rows.push([]);
      for (const cm of rm[2]!.matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const attrs = cm[1]!, inner = cm[2] ?? '';
        const ref = /\br="([A-Z]+)\d+"/.exec(attrs)?.[1];
        const type = /\bt="([^"]+)"/.exec(attrs)?.[1] ?? 'n';
        const v = /<v>([\s\S]*?)<\/v>/.exec(inner)?.[1];
        let val = '';
        if (type === 's' && v != null) val = shared[Number(v)] ?? '';
        else if (type === 'inlineStr') val = texts(inner);
        else if (type === 'b') val = v === '1' ? 'VRAI' : 'FAUX';
        else if (v != null) val = unxml(v);
        const j = ref ? colIndex(ref) : row.length;
        while (row.length < j) row.push('');
        row[j] = val;
      }
      rows.push(row);
    }
    out.push({ name: s.name, rows: trimRows(rows) });
  }
  return out;
}

/** Page web déguisée en .xls : la plus grande table de la page. */
export function parseHtmlTable(html: string): string[][] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const tables = [...doc.querySelectorAll('table')];
  const best = tables.sort((a, b) => b.rows.length - a.rows.length)[0];
  if (!best) return [];
  return trimRows([...best.rows].map(r => [...r.cells].map(c => c.textContent ?? '')));
}

/** Un fichier quelconque → ses feuilles. */
export async function readSheetFile(file: File): Promise<ReadResult> {
  const buf = new Uint8Array(await file.arrayBuffer());
  const head = buf.subarray(0, 8);
  if (head[0] === 0x50 && head[1] === 0x4b) return { sheets: await parseXlsx(buf), kind: 'xlsx' };
  if (head[0] === 0xd0 && head[1] === 0xcf && head[2] === 0x11 && head[3] === 0xe0) {
    throw new Error('Ancien format Excel (.xls) : ouvrez-le et enregistrez-le en .xlsx ou en .csv.');
  }
  const { text, encoding } = decodeText(buf);
  if (/^\s*</.test(text) && /<table/i.test(text)) return { sheets: [{ name: file.name, rows: parseHtmlTable(text) }], kind: 'html', encoding };
  return { sheets: [{ name: file.name, rows: parseCsv(text) }], kind: 'csv', encoding };
}

/** Copier-coller depuis un tableur : des tabulations. */
export function readPasted(text: string): ReadResult {
  return { sheets: [{ name: 'Collé', rows: text.includes('\t') ? trimRows(text.split(/\r?\n/).map(l => l.split('\t'))) : parseCsv(text) }], kind: 'paste' };
}
